import type { WebhookDeliveryStatus } from '@zeus/index'
import { RefreshCw, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { tourAnchor } from '~/helpers/tour/anchors'

type Delivery = {
  id: string
  event: string
  status: `${WebhookDeliveryStatus}`
  attempts: number
  lastStatusCode?: number | null
  lastError?: string | null
  createdAt: unknown
  nextAttemptAt?: unknown
}

type IProps = {
  deliveries: readonly Delivery[]
  onRefresh: () => void
  onTest: () => void
  /** Un test n'a de sens que vers une destination active. */
  canTest: boolean
}

const STATUS_VARIANT = { PENDING: 'secondary', SENDING: 'secondary', DELIVERED: 'default', FAILED: 'destructive' } as const

/** Les derniers appels et leur sort (#5), et l'appel de test. */
function WebhookDeliveriesCard({ deliveries, onRefresh, onTest, canTest }: IProps) {
  const { t, i18n } = useTranslation()

  return (
    <Card {...tourAnchor('webhooks.deliveries')}>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle>{t('webhooks.deliveries.title')}</CardTitle>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t('common.refresh')}>
            <RefreshCw className="size-4" />
          </Button>
          <Button type="button" variant="outline" onClick={onTest} disabled={!canTest} {...tourAnchor('webhooks.test')}>
            <Send className="mr-2 size-4" />
            {t('webhooks.test')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {deliveries.length === 0
          ? <p className="p-4 text-center text-sm text-muted-foreground">{t('webhooks.deliveries.empty')}</p>
          : (
              <Table aria-label={t('webhooks.deliveries.title')}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('webhooks.deliveries.date')}</TableHead>
                    <TableHead>{t('webhooks.deliveries.event')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('webhooks.deliveries.attempts')}</TableHead>
                    <TableHead>{t('webhooks.deliveries.response')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deliveries.map(delivery => (
                    <TableRow key={delivery.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {new Date(String(delivery.createdAt)).toLocaleString(i18n.language)}
                      </TableCell>
                      <TableCell><code className="text-xs">{delivery.event}</code></TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[delivery.status]}>{t(`webhooks.status.${delivery.status}`)}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{delivery.attempts}</TableCell>
                      <TableCell className="max-w-[16rem] text-xs text-muted-foreground">
                        {delivery.lastStatusCode ?? (delivery.lastError ? t(`webhooks.error.${delivery.lastError}`) : '—')}
                        {delivery.status === 'PENDING' && delivery.attempts > 0 && (
                          <span className="block">
                            {t('webhooks.deliveries.nextAttempt', { date: new Date(String(delivery.nextAttemptAt)).toLocaleString(i18n.language) })}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </CardContent>
    </Card>
  )
}

export default WebhookDeliveriesCard
